import { useLayoutEffect, useRef, useState } from "react";
import type { Dispatch, RefObject, SetStateAction } from "react";

export type VisualViewportRect = {
  readonly height: number;
  readonly left: number;
  readonly top: number;
  readonly width: number;
};

const MIN_VIEWPORT_HEIGHT_PX = 320;
const MIN_VIEWPORT_WIDTH_PX = 320;
const FALLBACK_VIEWPORT_HEIGHT_PX = 568;
const FALLBACK_VIEWPORT_WIDTH_PX = 360;

export function readVisualViewportRect(): VisualViewportRect {
  if (typeof window === "undefined") {
    return fallbackVisualViewportRect();
  }
  const layoutWidth = positiveViewportDimension(
    window.innerWidth,
    FALLBACK_VIEWPORT_WIDTH_PX,
    MIN_VIEWPORT_WIDTH_PX,
  );
  const layoutHeight = positiveViewportDimension(
    window.innerHeight,
    FALLBACK_VIEWPORT_HEIGHT_PX,
    MIN_VIEWPORT_HEIGHT_PX,
  );
  return rectFromWindowViewport(layoutWidth, layoutHeight);
}

export function useVisualViewportRect(active = true): VisualViewportRect {
  const [rect, setRect] = useState(readVisualViewportRect);
  const rafIdRef = useRef<number | null>(null);
  useLayoutEffect(() => {
    if (!active || typeof window === "undefined") return undefined;
    const sync = (): void => scheduleViewportRectSync(setRect, rafIdRef);
    sync();
    return addVisualViewportListeners(sync);
  }, [active]);
  useLayoutEffect(() => () => cancelViewportRectSync(rafIdRef), []);
  return rect;
}

export function addVisualViewportListeners(listener: () => void): () => void {
  const options: AddEventListenerOptions = { passive: true };
  window.addEventListener("resize", listener, options);
  window.visualViewport?.addEventListener("resize", listener, options);
  window.visualViewport?.addEventListener("scroll", listener, options);
  return () => removeVisualViewportListeners(listener);
}

function rectFromWindowViewport(
  layoutWidth: number,
  layoutHeight: number,
): VisualViewportRect {
  const viewport = window.visualViewport;
  return {
    height: positiveViewportDimension(
      viewport?.height,
      layoutHeight,
      MIN_VIEWPORT_HEIGHT_PX,
    ),
    left: positiveOffset(viewport?.offsetLeft),
    top: positiveOffset(viewport?.offsetTop),
    width: positiveViewportDimension(
      viewport?.width,
      layoutWidth,
      MIN_VIEWPORT_WIDTH_PX,
    ),
  };
}

function fallbackVisualViewportRect(): VisualViewportRect {
  return {
    height: FALLBACK_VIEWPORT_HEIGHT_PX,
    left: 0,
    top: 0,
    width: FALLBACK_VIEWPORT_WIDTH_PX,
  };
}

function positiveViewportDimension(
  value: number | undefined,
  fallback: number,
  minimum: number,
): number {
  if (value === undefined || !Number.isFinite(value) || value < minimum) {
    return fallback;
  }
  return value;
}

function positiveOffset(value: number | undefined): number {
  if (value === undefined || !Number.isFinite(value)) return 0;
  return Math.max(0, value);
}

function scheduleViewportRectSync(
  setRect: Dispatch<SetStateAction<VisualViewportRect>>,
  rafIdRef: RefObject<number | null>,
): void {
  cancelViewportRectSync(rafIdRef);
  rafIdRef.current = window.requestAnimationFrame(() => {
    setRect(readVisualViewportRect());
    rafIdRef.current = null;
  });
}

function cancelViewportRectSync(rafIdRef: RefObject<number | null>): void {
  if (rafIdRef.current === null) return;
  window.cancelAnimationFrame(rafIdRef.current);
  rafIdRef.current = null;
}

function removeVisualViewportListeners(listener: () => void): void {
  window.removeEventListener("resize", listener);
  window.visualViewport?.removeEventListener("resize", listener);
  window.visualViewport?.removeEventListener("scroll", listener);
}
